import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApp } from '../context/AppContext';
import { packagesService } from '../services/packagesService';
import { XMarkIcon } from '@heroicons/react/24/outline';

const PackageSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .required('Name is required'),
  description: Yup.string()
    .min(10, 'Description must be at least 10 characters')
    .required('Description is required'),
  price: Yup.number()
    .min(0, 'Price must be positive')
    .required('Price is required'),
});

const PackageForm = ({ packageData, isEdit = false }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  const formik = useFormik({
    initialValues: {
      name: packageData?.name || '',
      description: packageData?.description || '',
      price: packageData?.price || 0,
    },
    validationSchema: PackageSchema,
    onSubmit: async (values) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        if (isEdit && packageData) {
          const updatedPackage = await packagesService.updatePackage(packageData.id, values);
          dispatch({ type: 'UPDATE_PACKAGE', payload: updatedPackage });
        } else {
          const newPackage = await packagesService.createPackage(values);
          dispatch({ type: 'ADD_PACKAGE', payload: newPackage });
        }

        navigate('/packages');
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error.response?.data?.message || 'Operation failed' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
  });

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? 'Edit Package' : 'Create Package'}
              </h1>
              <p className="text-gray-600">
                {isEdit ? 'Update package information' : 'Add a new package to your portfolio'}
              </p>
            </div>
            <button
              onClick={() => navigate('/packages')}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        <div className="card">
          <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
            {state.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{state.error}</p>
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Package Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                className={`input-field mt-1 ${
                  formik.touched.name && formik.errors.name
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : ''
                }`}
                placeholder="Enter package name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.name && formik.errors.name && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                className={`input-field mt-1 ${
                  formik.touched.description && formik.errors.description
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : ''
                }`}
                placeholder="Describe your package"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.description}</p>
              )}
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                Price (USD) *
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  className={`input-field pl-7 ${
                    formik.touched.price && formik.errors.price
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : ''
                  }`}
                  placeholder="0.00"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.price && formik.errors.price && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.price}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/packages')}
                className="btn-secondary"
                disabled={state.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={state.isLoading || !formik.isValid}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  isEdit ? 'Update Package' : 'Create Package'
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PackageForm;
